import React, { FC, useEffect, useState } from 'react';
import { useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import CircularProgress from '@mui/material/CircularProgress';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import { useStyles } from './useStyles';
import { useTranslation } from 'next-i18next';
import {
  getEnumOrganizations,
  useAPIQuery,
  usePlanForms,
  usePlanStatuses,
  usePlanTypes,
} from '@web-kc/core/api';
import { ControlledMaskInput, ControlledTabs, CModal } from '@web-kc/shared/ui';
import { Controller, useForm } from 'react-hook-form';
import { useApp } from '@web-kc/core/providers';
import { usePlanList } from '../../providers';

interface Props {
  open: boolean;
  handleClose: () => void;
  appliedFilters?: Record<string, number | string | undefined>;
  setAppliedFilters?: (
    values: Record<string, number | string | undefined>,
  ) => void;
}

// Вынесли компонент наружу чтобы избежать потери фокуса при рендере
const FilterSection = ({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) => (
  <Stack
    direction={{ lg: 'row', md: 'column', xs: 'column' }}
    gap={{ xs: 1.25, lg: 2.5 }}
    alignItems={{ xs: 'flex-start', lg: 'center' }}
  >
    <Typography
      width="100%"
      maxWidth={120}
      variant="body2"
      fontWeight={700}
      sx={{ minWidth: { xs: 'auto', lg: '120px' } }}
    >
      {title}
    </Typography>
    <Box sx={{ width: '100%', flex: 1 }}>{children}</Box>
  </Stack>
);

const SearchFiltersModal: FC<Props> = ({ open, handleClose }) => {
  const { t } = useTranslation(['common', 'plans']);
  const theme = useTheme();
  const isKC = useApp() === 'kc';
  const {
    searchFilters,
    setSearchFilters,
    companies,
    isLoadingCompanies,
    companyById,
    debouncedSearchCompanies,
  } = usePlanList();
  const { data: dataForms, isLoading: isLoadingForms } = usePlanForms();
  const { data: dataStatuses, isLoading: isLoadingStatuses } =
    usePlanStatuses();
  const { data: dataTypes, isLoading: isLoadingTypes } = usePlanTypes();
  const { data: organizations, isLoading: isLoadingOrganizations } =
    useAPIQuery(getEnumOrganizations);

  const [openCompanies, setOpenCompanies] = useState(false);
  const [companySearchValue, setCompanySearchValue] = useState('');
  const [selectedCompanyData, setSelectedCompanyData] = useState<{
    id: number;
    name: string;
    fullName?: string;
  } | null>(null);

  const { handleSubmit, control, watch, reset, setValue } = useForm<
    Record<string, string | undefined>
  >({});

  const { classes } = useStyles({ theme });

  const selectedCompany = watch('company');

  useEffect(
    () =>
      reset({
        term: searchFilters.term
          ? (searchFilters.term as never)
          : !isKC
            ? ('YEARLY' as never) // Если RTI - устанавливаем YEARLY по умолчанию
            : (null as never),
        status: searchFilters.status
          ? (String(searchFilters.status) as never)
          : ('' as never),
        form: searchFilters.form
          ? (searchFilters.form as never)
          : (null as never),
        organization: searchFilters.organization || (null as never),
        company: searchFilters.company
          ? (String(searchFilters.company) as never)
          : ('' as never),
        sumFrom: searchFilters.sumFrom
          ? (String(searchFilters.sumFrom) as never)
          : ('' as never),
        sumTo: searchFilters.sumTo
          ? (String(searchFilters.sumTo) as never)
          : ('' as never),
        yearBefore: searchFilters.yearBefore
          ? (String(searchFilters.yearBefore) as never)
          : ('' as never),
        yearAfter: searchFilters.yearAfter
          ? (String(searchFilters.yearAfter) as never)
          : ('' as never),
      }),
    [reset, searchFilters, isKC],
  );

  // Синхронизация selectedCompanyData с компанией из URL или списка
  useEffect(() => {
    if (selectedCompany && !selectedCompanyData) {
      if (companyById) {
        setSelectedCompanyData({
          id: companyById.id || 0,
          name: companyById.name || '',
          fullName: companyById.fullName,
        });
      } else {
        const company = companies?.content?.find(
          (c) => c.id === Number(selectedCompany),
        );
        if (company) {
          setSelectedCompanyData({
            id: company.id || 0,
            name: company.name || '',
            fullName: company.fullName,
          });
        }
      }
    }
  }, [selectedCompany, selectedCompanyData, companyById, companies?.content]);

  const handleAddCompany = (companyId: number) => {
    const company = companies?.content?.find((c) => c.id === companyId);
    if (company) {
      setSelectedCompanyData({
        id: company.id || 0,
        name: company.name || '',
        fullName: company.fullName,
      });
    }
    setValue('company', String(companyId));
    setOpenCompanies(false);
    setCompanySearchValue('');
  };

  const handleRemoveCompany = () => {
    setValue('company', '');
    setCompanySearchValue('');
    setSelectedCompanyData(null);
  };

  const onSubmit = (values: Record<string, string | undefined>) => {
    // Список технических параметров, которые всегда сохраняем
    const technicalParams = ['page', 'size', 'sort', 'modal'];

    // Получаем список всех ключей фильтров (не технических)
    const allFilterKeys = Object.keys(searchFilters).filter(
      (key) => !technicalParams.includes(key),
    );

    // Фильтруем только заполненные значения
    const filteredValues = Object.entries(values).reduce(
      (acc, [key, value]) => {
        // Включаем только непустые значения (и не null)
        if (value !== undefined && value !== null && value !== '') {
          acc[key] = value;
        }
        return acc;
      },
      {} as Record<string, string | undefined>,
    );

    // Если RTI - принудительно устанавливаем term = 'YEARLY'
    if (!isKC) {
      filteredValues.term = 'YEARLY';
    }

    // Создаем объект, где старые фильтры = undefined (удаляются), новые = их значения
    const clearedOldFilters = allFilterKeys.reduce(
      (acc, key) => {
        acc[key] = undefined; // Удаляем старые фильтры
        return acc;
      },
      {} as Record<string, undefined>,
    );

    // Сохраняем технические параметры, удаляем старые фильтры, добавляем новые
    setSearchFilters({
      page: 0,
      size: searchFilters.size,
      sort: searchFilters.sort,
      ...clearedOldFilters,
      ...filteredValues,
    } as never).then(() => handleClose());
  };

  const handleResetFilters = () => {
    // Список технических параметров
    const technicalParams = ['page', 'size', 'sort', 'modal'];

    // Получаем список всех ключей фильтров для удаления
    const allFilterKeys = Object.keys(searchFilters).filter(
      (key) => !technicalParams.includes(key),
    );

    // Создаем объект где все фильтры = undefined
    const clearedFilters = allFilterKeys.reduce(
      (acc, key) => {
        acc[key] = undefined;
        return acc;
      },
      {} as Record<string, undefined>,
    );

    // Если RTI - устанавливаем term = 'YEARLY' даже при сбросе
    const finalFilters = !isKC
      ? { ...clearedFilters, term: 'YEARLY' }
      : clearedFilters;

    setSearchFilters({
      page: searchFilters.page,
      size: searchFilters.size,
      sort: 'CREATED_DESC',
      ...finalFilters,
    } as never).then(() => handleClose());
  };

  if (isLoadingForms || isLoadingStatuses || isLoadingTypes) return null;

  return (
    <CModal
      open={open}
      onClose={handleClose}
      modalTitle={t('plans:filter.searchFilters')}
      maxWidth="md"
      fullWidth
      footer={
        <Stack
          direction="row"
          spacing={2}
          sx={{
            '& .MuiButton-root': {
              flex: 1,
            },
          }}
        >
          <Box>
            <Button variant="outlined" onClick={handleResetFilters}>
              {t('plans:filter.resetButton')}
            </Button>
          </Box>
          <Box>
            <Button
              type="submit"
              variant="contained"
              color={isKC ? 'primary' : 'secondary'}
              onClick={handleSubmit(onSubmit)}
            >
              {t('plans:filter.searchButton')}
            </Button>
          </Box>
        </Stack>
      }
    >
      <Box
        component="form"
        onSubmit={handleSubmit(onSubmit)}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 3,
        }}
      >
        {isKC && (
          <FilterSection title={t('plans:organization')}>
            <Box className={classes.tabWrapper}>
              <ControlledTabs
                name="organization"
                tabs={[
                  { id: null, name: 'plans:filter.any' },
                  ...(organizations ?? []),
                ]}
                loading={isLoadingOrganizations}
                sx={{ overflowX: 'auto' }}
                control={control}
                tabLabelKey="name"
                tabValueKey="id"
                withTranslation
              />
            </Box>
          </FilterSection>
        )}

        <FilterSection title={t('plans:company')}>
          <ClickAwayListener onClickAway={() => setOpenCompanies(false)}>
            <div>
              <Box width={'100%'}>
                <TextField
                  fullWidth
                  autoComplete="off"
                  placeholder={t('plans:filter.searchCompany')}
                  value={(() => {
                    if (
                      selectedCompany &&
                      !companySearchValue &&
                      selectedCompanyData
                    ) {
                      return (
                        selectedCompanyData.fullName ||
                        selectedCompanyData.name ||
                        ''
                      );
                    }
                    return companySearchValue;
                  })()}
                  onClick={() => setOpenCompanies(true)}
                  onChange={(e) => {
                    setCompanySearchValue(e.target.value);
                    debouncedSearchCompanies(e.target.value);
                  }}
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      ),
                      endAdornment: selectedCompany && !companySearchValue && (
                        <InputAdornment position="end">
                          <IconButton
                            size="small"
                            onClick={handleRemoveCompany}
                            sx={{ padding: '2px' }}
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </InputAdornment>
                      ),
                    },
                  }}
                />

                {openCompanies && companySearchValue && (
                  <Box
                    sx={{
                      overflow: 'auto',
                      backgroundColor: 'white',
                      zIndex: 999,
                      maxHeight: '200px',
                      position: 'absolute',
                      minWidth: 500,
                      width: '100%',
                      borderRadius: '4px',
                      boxShadow: '0px 2px 5px -3px rgba(0,0,0,0.7)',
                      border: '1px solid #e0e0e0',
                    }}
                  >
                    {isLoadingCompanies ? (
                      <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="center"
                        p={2}
                      >
                        <CircularProgress size={24} />
                      </Stack>
                    ) : (
                      <List disablePadding>
                        {companies?.content?.length === 0 ? (
                          <ListItem>
                            <ListItemText
                              primary={t('plans:filter.noCompaniesFound')}
                            />
                          </ListItem>
                        ) : (
                          companies?.content
                            ?.filter(
                              (company) =>
                                company.id !== Number(selectedCompany),
                            )
                            .map((company) => (
                              <ListItemButton
                                key={company.id}
                                onClick={() =>
                                  company.id && handleAddCompany(company.id)
                                }
                              >
                                <ListItemText
                                  primary={company.fullName || company.name}
                                  disableTypography
                                />
                              </ListItemButton>
                            ))
                        )}
                      </List>
                    )}
                  </Box>
                )}
              </Box>
            </div>
          </ClickAwayListener>
        </FilterSection>

        <FilterSection title={t('plans:year')}>
          <Box display="flex" alignItems="center" gap="10px" width="100%">
            <ControlledMaskInput
              type="number"
              name="yearAfter"
              control={control}
              min={1900}
              max={2099}
              scale={0}
              sx={{ flex: 1 }}
              fullWidth
            />
            <Typography>—</Typography>
            <ControlledMaskInput
              type="number"
              name="yearBefore"
              control={control}
              min={1900}
              max={2099}
              scale={0}
              sx={{ flex: 1 }}
              fullWidth
            />
          </Box>
        </FilterSection>

        <FilterSection title={t('plans:term')}>
          <Box className={classes.tabWrapper}>
            <ControlledTabs
              name="term"
              tabs={[
                { id: null, name: t('plans:filter.any') },
                ...(dataTypes ?? []),
              ]}
              control={control}
              tabValueKey="id"
              tabLabelKey="name"
              disabled={!isKC}
            />
          </Box>
        </FilterSection>

        {isKC && (
          <FilterSection title={t('plans:form')}>
            <Box className={classes.tabWrapper}>
              <ControlledTabs
                name="form"
                tabs={[
                  { id: null, name: t('plans:filter.any') },
                  ...(dataForms ?? []),
                ]}
                control={control}
                tabValueKey="id"
                tabLabelKey="name"
              />
            </Box>
          </FilterSection>
        )}

        <FilterSection title={t('plans:filter.sum')}>
          <Box display="flex" alignItems="center" gap="10px" width="100%">
            <ControlledMaskInput
              type="number"
              name="sumFrom"
              control={control}
              // max={+(watch('sumTo') as string) || Infinity}
              placeholder={t('plans:filter.from')}
              sx={{ flex: 1 }}
              fullWidth
            />
            <Typography>—</Typography>
            <ControlledMaskInput
              type="number"
              name="sumTo"
              control={control}
              placeholder={t('plans:filter.to')}
              // min={+(watch('sumFrom') as string) || 1}
              sx={{ flex: 1 }}
              fullWidth
            />
          </Box>
        </FilterSection>

        <FilterSection title={t('plans:status')}>
          <Controller
            name="status"
            control={control}
            render={({ field }) => (
              <Select
                {...field}
                sx={{ width: '100%', height: '44px' }}
                displayEmpty={true}
                defaultValue=""
                MenuProps={{ className: classes.menu }}
              >
                <MenuItem value="" key="nonecat">
                  {t('plans:filter.any')}
                </MenuItem>
                {dataStatuses?.map((category) => (
                  <MenuItem
                    key={category.id}
                    value={category.id}
                    sx={{ wordBreak: 'break-all;' }}
                  >
                    {category.name}
                  </MenuItem>
                ))}
              </Select>
            )}
          />
        </FilterSection>
      </Box>
    </CModal>
  );
};

export default SearchFiltersModal;
